import { create } from "zustand";
import { persist } from "zustand/middleware";

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  isDark: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  // Deprecated - keeping for backward compatibility
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
}

// Helper to detect system preference
const getSystemPreference = (): boolean => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

// Helper to apply theme
const applyTheme = (isDark: boolean) => {
  if (typeof document !== 'undefined') {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }
};

// Helper to resolve actual theme based on mode
const resolveTheme = (mode: ThemeMode): boolean => {
  if (mode === 'system') {
    return getSystemPreference();
  }
  return mode === 'dark';
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      isDark: getSystemPreference(),
      
      setThemeMode: (mode: ThemeMode) => {
        const isDark = resolveTheme(mode);
        set({ mode, isDark });
        applyTheme(isDark);
      },
      
      // Deprecated - keeping for backward compatibility
      toggleTheme: () => {
        const currentMode = get().mode;
        const newMode: ThemeMode = currentMode === 'dark' ? 'light' : 'dark';
        get().setThemeMode(newMode);
      },
      
      // Deprecated - keeping for backward compatibility
      setTheme: (isDark: boolean) => {
        const mode: ThemeMode = isDark ? 'dark' : 'light';
        set({ mode, isDark });
        applyTheme(isDark);
      },
    }),
    {
      name: "theme-storage",
      onRehydrateStorage: () => (state) => {
        // Apply theme on app load and listen for system changes
        if (state) {
          const isDark = resolveTheme(state.mode);
          applyTheme(isDark);
          
          // Listen for system theme changes if mode is 'system'
          if (state.mode === 'system' && typeof window !== 'undefined') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handler = (e: MediaQueryListEvent) => {
              const store = useThemeStore.getState();
              if (store.mode === 'system') {
                store.setThemeMode('system');
              }
            };
            mediaQuery.addEventListener('change', handler);
          }
        }
      },
    }
  )
);
